import axios from 'axios';

// Dropbox API configuration
const DROPBOX_ACCESS_TOKEN = import.meta.env.VITE_DROPBOX_ACCESS_TOKEN || '';
const DROPBOX_APP_KEY = import.meta.env.VITE_DROPBOX_APP_KEY || '';
const DROPBOX_APP_SECRET = import.meta.env.VITE_DROPBOX_APP_SECRET || '';
const DROPBOX_BASE_URL = 'https://content.dropboxapi.com/2';

// Validate required environment variables
if (!DROPBOX_ACCESS_TOKEN) {
  console.warn('Dropbox access token is not configured. Please set VITE_DROPBOX_ACCESS_TOKEN environment variable.');
}

class DropboxService {
  constructor() {
    this.accessToken = DROPBOX_ACCESS_TOKEN;
    this.appKey = DROPBOX_APP_KEY;
    this.appSecret = DROPBOX_APP_SECRET;
    this.baseURL = DROPBOX_BASE_URL;
  }

  // Check if Dropbox is configured
  isConfigured() {
    return !!this.accessToken;
  }

  // Upload file to Dropbox
  async uploadFile(file, path = null) {
    if (!this.isConfigured()) {
      throw new Error('Dropbox is not configured. Please check environment variables.');
    }

    try {
      const filePath = path || `/uploads/${Date.now()}-${file.name}`;
      
      const response = await axios.post(
        `${this.baseURL}/files/upload`,
        file,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/octet-stream',
            'Dropbox-API-Arg': JSON.stringify({
              path: filePath,
              mode: 'add',
              autorename: true,
              mute: false,
            }),
          },
        }
      );

      // Create shared link for the uploaded file
      const sharedLink = await this.createSharedLink(response.data.path_lower);
      
      return {
        id: response.data.id,
        name: response.data.name,
        path_lower: response.data.path_lower,
        shared_link: sharedLink.url,
        size: response.data.size,
      };
    } catch (error) {
      console.error('Dropbox upload error:', error);
      throw new Error(`Failed to upload file to Dropbox: ${error.response?.data?.error_summary || error.message}`);
    }
  }

  // Create shared link for a file
  async createSharedLink(path) {
    try {
      const response = await axios.post(
        `${this.baseURL.replace('content', 'api')}/sharing/create_shared_link_with_settings`,
        {
          path: path,
          settings: {
            requested_visibility: 'public',
            allow_download: true,
          },
        },
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return {
        url: response.data.url,
        id: response.data.id,
      };
    } catch (error) {
      // If link already exists, try to get existing link
      if (error.response?.data?.error_summary?.includes('shared_link_already_exists')) {
        return await this.listSharedLinks(path);
      }
      throw error;
    }
  }

  // List existing shared links
  async listSharedLinks(path) {
    try {
      const response = await axios.post(
        `${this.baseURL.replace('content', 'api')}/sharing/list_shared_links`,
        {
          path: path,
          direct_only: true,
        },
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.links && response.data.links.length > 0) {
        return {
          url: response.data.links[0].url,
          id: response.data.links[0].id,
        };
      }
      
      throw new Error('No shared links found');
    } catch (error) {
      throw new Error(`Failed to list shared links: ${error.message}`);
    }
  }

  // Delete file from Dropbox
  async deleteFile(path) {
    try {
      await axios.post(
        `${this.baseURL.replace('content', 'api')}/files/delete_v2`,
        { path: path },
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      return true;
    } catch (error) {
      throw new Error(`Failed to delete file: ${error.message}`);
    }
  }

  // Get file metadata
  async getMetadata(path) {
    try {
      const response = await axios.post(
        `${this.baseURL.replace('content', 'api')}/files/get_metadata`,
        { path: path },
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to get file metadata: ${error.message}`);
    }
  }
}

// Create singleton instance
const dropboxService = new DropboxService();

export default dropboxService;